'use client';

import { FC, useCallback, useMemo, useState } from 'react';

import { getContentfulProductsByIds } from '@ravago/shared/next-data-access/client';
import { TextColour, TextComponent, TextVariant } from '@ravago/shared/page-data/models/base';
import { UnitSystem } from '@ravago/shared/page-data/models/config';
import { NormalizedProduct, NormalizedUnitOfMeasure } from '@ravago/shared/page-data/models/contentful-mapped';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Dropdown, DropdownOption } from '@ravago/shared/radiance/components/input-field/components/Dropdown/Dropdown';
import { Spinner } from '@ravago/shared/radiance/components/spinner/components/Spinner/Spinner';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useBreakpoint } from '@ravago/shared/radiance/hooks/useBreakpoint/useBreakpoint';
import { useTranslations } from '@ravago/shared/translations/client';

import { useQuery } from '@tanstack/react-query';

import { usePathLanguageCode } from '../../../hooks/use-path-locale';
import { useProductCompare } from '../../../hooks/use-product-compare';
import NotFoundIllustration from '../../illustrations/not-found-illustration';
import { EmptyState } from '../../shared/empty-state/empty-state.component';
import { unitSystems } from '../../shared/physical-property-display/mappers/unit-systems.mapper';
import { DynamicPageLink } from '../dynamic-page-link/dynamic-page-link';

import { ProductCompareActions } from './components/product-compare-actions';
import { ProductCompareTable } from './components/product-compare-table';

const QUERY_KEY = 'productCompareProducts';

interface TitleData {
   value: string;
   component?: TextComponent;
   color?: TextColour;
   variant?: TextVariant;
}

interface Props {
   locale: string;
   productsBaseRoute: string;
   industryBaseRoute: string;
   titleData: TitleData;
   channelUOMs?: NormalizedUnitOfMeasure[];
   sharedProducts: NormalizedProduct[] | undefined;
   getProspectorUrlForSellers: (products: NormalizedProduct[]) => Promise<string | undefined>;
}

export const ProductCompareContent: FC<Props> = ({
   locale,
   productsBaseRoute,
   industryBaseRoute,
   titleData,
   channelUOMs = [],
   sharedProducts,
   getProspectorUrlForSellers
}) => {
   const translate = useTranslations();
   const languageCode = usePathLanguageCode();
   const { productItems: localProductItems, isInitialized } = useProductCompare();
   const { desktop } = useBreakpoint('desktop');
   const units = unitSystems(translate);
   const defaultUom = channelUOMs.find((uom) => uom.default)?.id === 'imperial' ? 'US' : 'SI';
   const [unitSystem, setUnitSystem] = useState<UnitSystem>(() => units.find((unit) => unit.id === defaultUom)?.id ?? units[0].id);
   const selectedUnitSystem = useMemo(() => units.find((unit) => unit.id === unitSystem), [unitSystem, units]);

   // sharedProducts are provided when accessing via URL params (shared comparisons)
   const hasSharedProducts = !!sharedProducts?.length;

   // Use shared product IDs or fall back to localStorage products
   const productIds = useMemo(() => {
      if (hasSharedProducts) {
         return sharedProducts.map((product) => product.id);
      }
      return localProductItems.map((item) => item.id);
   }, [hasSharedProducts, sharedProducts, localProductItems]);

   // Sort productIds to create a stable query key (prevents refetch on reorder)
   const sortedProductIds = useMemo(() => [...productIds].sort((a, b) => a.localeCompare(b)), [productIds]);

   const {
      data: fetchedProducts = [],
      isLoading: isQueryLoading,
      error
   } = useQuery({
      queryKey: [QUERY_KEY, languageCode, sortedProductIds],
      queryFn: async (): Promise<NormalizedProduct[]> => {
         if (productIds.length === 0) return [];

         const result = await getContentfulProductsByIds(languageCode, productIds);
         return result ?? [];
      },
      // Skip fetching if server already prefetched products (shared comparison via URL)
      enabled: !hasSharedProducts && isInitialized && productIds.length > 0,
      placeholderData: (previousData) => previousData
   });

   // Use server-prefetched products when available, otherwise use client-fetched products
   const availableProducts = hasSharedProducts ? sharedProducts : fetchedProducts;

   // Filter products based on current productIds (handles removes without refetching)
   const products = useMemo(() => availableProducts.filter((product) => productIds.includes(product.id)), [availableProducts, productIds]);

   // Show loading only on initial client-side load (not for server-prefetched or on removes)
   const isLoading = !hasSharedProducts && (!isInitialized || (isQueryLoading && fetchedProducts.length === 0));

   // Generate shareable URL based on current products being displayed
   const getShareableUrl = useCallback(() => {
      if (typeof window === 'undefined' || products.length === 0) return '';

      const productsParam = products.map((product) => product.id).join(',');
      const url = new URL(window.location.href);
      // Clear existing params and set the products param
      url.search = '';
      url.searchParams.set('products', productsParam);
      return url.toString();
   }, [products]);

   const switchUnitSystem = (value?: DropdownOption) => {
      if (value) setUnitSystem(value.id as UnitSystem);
   };

   const renderTitle = () => {
      if (!titleData.value) return null;

      return (
         <Typography variant={titleData.variant} color={titleData.color} component={titleData.component}>
            {titleData.value}
         </Typography>
      );
   };

   if (isLoading) {
      return (
         <Box direction="column" gap="md" width="100%">
            {renderTitle()}
            <Box direction="column" items="center">
               <Spinner />
            </Box>
         </Box>
      );
   }

   if (error) {
      return null;
   }

   if (products.length < 2) {
      return (
         <Box direction="column" gap="md" width="100%">
            {renderTitle()}
            <Box direction="column" justify="center" items="center">
               <EmptyState
                  title={translate('page-consumer.product-compare.no-products-selected')}
                  label={translate('page-consumer.product-compare.no-products-selected-description')}
                  image={<NotFoundIllustration />}
                  button={
                     <DynamicPageLink
                        id="dynamic-link__product-compare-avatar"
                        baseRoute={{ url: productsBaseRoute.replace('/:slug', ''), noFollow: true }}
                        locale={locale}
                        underline="none"
                     >
                        <Button leadingIcon="long-arrow-left" variant="secondary">
                           {translate('page-consumer.product-compare.find-products-to-compare')}
                        </Button>
                     </DynamicPageLink>
                  }
               />
            </Box>
         </Box>
      );
   }

   return (
      <Box direction="column" width="100%">
         <Box direction={desktop ? 'row' : 'column'} justify="between">
            {renderTitle()}
            <ProductCompareActions
               products={products}
               getShareableUrl={getShareableUrl}
               getProspectorUrlForSellers={getProspectorUrlForSellers}
            />
         </Box>
         <Box justify="between">
            {!hasSharedProducts && (
               <DynamicPageLink
                  id="dynamic-link__product-compare-avatar"
                  baseRoute={{ url: productsBaseRoute.replace('/:slug', ''), noFollow: true }}
                  locale={locale}
                  underline="none"
               >
                  <Button leadingIcon="long-arrow-left" variant="secondary">
                     {translate('page-consumer.product-compare.change-selection')}
                  </Button>
               </DynamicPageLink>
            )}
            <Box justify="end">
               <Dropdown
                  id="product-compare-unit-of-measure-dropdown"
                  emptyOption={false}
                  label={translate('product.information.physical-properties.unit-of-measure')}
                  options={units}
                  value={selectedUnitSystem}
                  onChange={switchUnitSystem}
               />
            </Box>
         </Box>
         <ProductCompareTable
            locale={locale}
            productsBaseRoute={productsBaseRoute}
            industryBaseRoute={industryBaseRoute}
            products={products}
            hasSharedProducts={hasSharedProducts}
            unitSystem={unitSystem}
         />
      </Box>
   );
};
